import { FieldType, KeyValuePairStringIntFieldValue } from '.';
import { InputValidationResult } from '..';
import {
  FieldTypeEnumResponse,
  PositionResponse,
  ValidationRuleResponse,
  ValidationTypeEnumResponse,
} from '../../.generated/admin-api';
import { apiTranslate } from '../../translations/translations';

const getFieldNumberValues = (
  field: FieldType,
  value: unknown,
): number[] | undefined => {
  switch (field.fieldType) {
    case FieldTypeEnumResponse.INTEGER:
    case FieldTypeEnumResponse.DOUBLE:
      return value !== undefined ? [Number(value)] : [];
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairs = value as KeyValuePairStringIntFieldValue;
      return keyValuePairs
        ? Object.keys(keyValuePairs).map((key) => keyValuePairs[key])
        : undefined;
    }
    case FieldTypeEnumResponse.ARRAY_OF_INT:
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
      return value as Array<number>;
    case FieldTypeEnumResponse.BOOLEAN:
    case FieldTypeEnumResponse.STRING:
    case FieldTypeEnumResponse.TEXT:
    case FieldTypeEnumResponse.DATE:
    case FieldTypeEnumResponse.POSITION:
    case FieldTypeEnumResponse.IMAGE:
    case FieldTypeEnumResponse.OPTIONS:
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
    case FieldTypeEnumResponse.MANUAL_AREA:
    case FieldTypeEnumResponse.DEDUCTIONS:
    default:
      // eslint-disable-next-line no-console
      console.warn(
        'Get number values not supported for field type',
        field.fieldType,
      );
      return undefined;
  }
};

const getFieldValueSum = (
  field: FieldType,
  value: unknown,
): number | undefined => {
  switch (field.fieldType) {
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairs = value as KeyValuePairStringIntFieldValue;
      return Object.keys(keyValuePairs)
        .map((key) => keyValuePairs[key])
        .reduce((sum, value) => sum + (value ?? 0), 0);
    }
    case FieldTypeEnumResponse.BOOLEAN:
    case FieldTypeEnumResponse.STRING:
    case FieldTypeEnumResponse.TEXT:
    case FieldTypeEnumResponse.INTEGER:
    case FieldTypeEnumResponse.DOUBLE:
    case FieldTypeEnumResponse.DATE:
    case FieldTypeEnumResponse.POSITION:
    case FieldTypeEnumResponse.IMAGE:
    case FieldTypeEnumResponse.OPTIONS:
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
    case FieldTypeEnumResponse.ARRAY_OF_INT:
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
    case FieldTypeEnumResponse.MANUAL_AREA:
    case FieldTypeEnumResponse.DEDUCTIONS:
    default:
      // eslint-disable-next-line no-console
      console.warn('Sum not supported for field type', field.fieldType);
      return undefined;
  }
};

const getFieldValueCount = (
  field: FieldType,
  value: unknown,
): number | undefined => {
  switch (field.fieldType) {
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairs = value as KeyValuePairStringIntFieldValue;
      return keyValuePairs ? Object.keys(keyValuePairs).length : 0;
    }
    case FieldTypeEnumResponse.BOOLEAN:
    case FieldTypeEnumResponse.STRING:
    case FieldTypeEnumResponse.TEXT:
    case FieldTypeEnumResponse.INTEGER:
    case FieldTypeEnumResponse.DOUBLE:
    case FieldTypeEnumResponse.DATE:
    case FieldTypeEnumResponse.POSITION:
    case FieldTypeEnumResponse.IMAGE:
    case FieldTypeEnumResponse.OPTIONS:
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
    case FieldTypeEnumResponse.ARRAY_OF_INT:
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
    case FieldTypeEnumResponse.MANUAL_AREA:
    case FieldTypeEnumResponse.DEDUCTIONS:
    default:
      // eslint-disable-next-line no-console
      console.warn('Count not supported for field type', field.fieldType);
      return undefined;
  }
};

const getFieldValueLength = (
  field: FieldType,
  value: unknown,
): number | undefined => {
  switch (field.fieldType) {
    case FieldTypeEnumResponse.STRING: {
      const valueAsString = value as string;
      return valueAsString ? valueAsString.length : undefined;
    }
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT:
    case FieldTypeEnumResponse.BOOLEAN:
    case FieldTypeEnumResponse.TEXT:
    case FieldTypeEnumResponse.INTEGER:
    case FieldTypeEnumResponse.DOUBLE:
    case FieldTypeEnumResponse.DATE:
    case FieldTypeEnumResponse.POSITION:
    case FieldTypeEnumResponse.IMAGE:
    case FieldTypeEnumResponse.OPTIONS:
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
    case FieldTypeEnumResponse.ARRAY_OF_INT:
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
    case FieldTypeEnumResponse.MANUAL_AREA:
    case FieldTypeEnumResponse.DEDUCTIONS:
    default:
      // eslint-disable-next-line no-console
      console.warn('Length not supported for field type', field.fieldType);
      return undefined;
  }
};

const isFieldRequiredForPrePegistration = (field: FieldType): boolean => {
  if (!field.validationRules) {
    return false;
  }

  return field.validationRules?.some(
    (rule) =>
      rule.type === ValidationTypeEnumResponse.REQUIRED_FOR_PREREGISTRATION,
  );
};

const validateRequiredForPreRegistration = (
  field: FieldType,
  value: unknown,
): boolean => {
  if (value === undefined) {
    return false;
  }

  switch (field.fieldType) {
    case FieldTypeEnumResponse.TEXT:
    case FieldTypeEnumResponse.IMAGE:
    case FieldTypeEnumResponse.DATE:
      return value !== '';
    case FieldTypeEnumResponse.INTEGER:
    case FieldTypeEnumResponse.DOUBLE:
    case FieldTypeEnumResponse.BOOLEAN:
      return true;
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairs = value as KeyValuePairStringIntFieldValue;
      return Object.keys(keyValuePairs).length > 0;
    }
    case FieldTypeEnumResponse.POSITION: {
      const location = value as PositionResponse;
      return (
        location &&
        location.longitude !== undefined &&
        location.latitude !== undefined
      );
    }
    case FieldTypeEnumResponse.STRING:
    case FieldTypeEnumResponse.OPTIONS:
      return value !== '';
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
    case FieldTypeEnumResponse.ARRAY_OF_INT:
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
    case FieldTypeEnumResponse.MANUAL_AREA:
    case FieldTypeEnumResponse.DEDUCTIONS:
    default:
      // eslint-disable-next-line no-console
      console.warn(
        'Validate empty or undefined not supported for field type',
        field.fieldType,
      );
      return false;
  }
};

const validateMinValue = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const valuesAsNumbers = getFieldNumberValues(field, value);
  const minValue = validationRule.validationValue as number;

  if (!valuesAsNumbers) {
    return true;
  }

  for (const number of valuesAsNumbers) {
    if (number < minValue) {
      return false;
    }
  }

  return true;
};

const validateMaxValue = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const valuesAsNumbers = getFieldNumberValues(field, value);
  const minValue = validationRule.validationValue as number;

  if (!valuesAsNumbers) {
    return true;
  }

  for (const number of valuesAsNumbers) {
    if (number > minValue) {
      return false;
    }
  }

  return true;
};

const validateMinSum = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const count = getFieldValueCount(field, value);

  if (count === 0) {
    return true;
  }

  const sum = getFieldValueSum(field, value);

  if (sum === undefined) {
    return true;
  }

  if (sum < validationRule.validationValue) {
    return false;
  }

  return true;
};

const validateMaxSum = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const count = getFieldValueCount(field, value);

  if (count === 0) {
    return true;
  }

  const sum = getFieldValueSum(field, value);

  if (sum === undefined) {
    return true;
  }

  if (sum > validationRule.validationValue) {
    return false;
  }

  return true;
};

const validateMinAllowedOptions = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const count = getFieldValueCount(field, value);

  if (count === undefined) {
    return true;
  }

  // The value is not set and not required for pre registration, we skip the validation
  if (count === 0 && !isFieldRequiredForPrePegistration(field)) {
    return true;
  }

  if (count < validationRule.validationValue) {
    return false;
  }

  return true;
};

const validateMaxAllowedOptions = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const count = getFieldValueCount(field, value);

  if (count === undefined) {
    return true;
  }

  // The value is not set and not required for pre registration, we skip the validation
  if (count === 0 && !isFieldRequiredForPrePegistration(field)) {
    return true;
  }

  if (count > validationRule.validationValue) {
    return false;
  }

  return true;
};

const validateMinCharLength = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  const length = getFieldValueLength(field, value);

  if (length === undefined) {
    return true;
  }

  if (length < validationRule.validationValue) {
    return false;
  }

  return true;
};

const validateMaxCharLength = (
  field: FieldType,
  value: unknown,
  validationRule: ValidationRuleResponse,
): boolean => {
  // eslint-disable-next-line no-console
  console.log('max', validationRule.validationValue);
  const length = getFieldValueLength(field, value);

  if (length === undefined) {
    return true;
  }

  if (length > validationRule.validationValue) {
    return false;
  }

  return true;
};

const buildErrorValidationResult = (
  validationRule: ValidationRuleResponse,
): InputValidationResult => {
  return {
    isValid: false,
    errorMessage: apiTranslate(validationRule.validationText),
  };
};

export const validateField = (
  field: FieldType,
  value: unknown,
): InputValidationResult => {
  if (!field.validationRules) {
    return {
      isValid: true,
    };
  }

  for (const validationRule of field.validationRules) {
    switch (validationRule.type) {
      case ValidationTypeEnumResponse.MIN_VALUE: {
        if (!validateMinValue(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.MAX_VALUE: {
        if (!validateMaxValue(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.REQUIRED_FOR_PREREGISTRATION:
        if (!validateRequiredForPreRegistration(field, value)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      case ValidationTypeEnumResponse.REQUIRED_FOR_MEASUREMENT:
        // Validation not required for preregistration, we ignore
        break;
      case ValidationTypeEnumResponse.MIN_SUM: {
        if (!validateMinSum(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.MAX_SUM: {
        if (!validateMaxSum(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.MIN_ALLOWED_OPTIONS: {
        if (!validateMinAllowedOptions(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.MAX_ALLOWED_OPTIONS: {
        if (!validateMaxAllowedOptions(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      }
      case ValidationTypeEnumResponse.MIN_CHAR_LENGTH:
        if (!validateMinCharLength(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      case ValidationTypeEnumResponse.MAX_CHAR_LENGTH:
        if (!validateMaxCharLength(field, value, validationRule)) {
          return buildErrorValidationResult(validationRule);
        }
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(
          'Unsupported validation type',
          validationRule.type,
          validationRule,
        );
        break;
    }
  }

  return {
    isValid: true,
  };
};
